/**
 * MessageList Component
 * Handles message rendering, streaming, and scroll management
 *
 * Responsibilities:
 * - Render user and assistant message bubbles
 * - Stream text progressively into messages
 * - Manage auto-scroll behavior
 * - Track message history
 */

import { eventBus } from '../core/utils.js';
import { TruncationManager } from '../utils/TruncationManager.js';
import { fadeIn } from '../utils/animation.js';
import { Tooltip } from '../utils/Tooltip.js';
import { renderInterleavedBlocks, toCopyMarkdown, REPLAY_SKIP_TAGS } from './structuredBlockRenderers.js';

// Must match scroll-margin-top on .message (chat.css) — the resting offset of a
// pinned message below the container top.
const SCROLL_TOP_MARGIN = 16;

// How far (px) the pinned message may drift from its resting offset while still
// counting as "at the pinned frame" for recompute/re-pin purposes.
const PIN_FRAME_TOLERANCE = 24;

// How far (px) the scroller may move out from under an in-flight pin tween
// before it is treated as user input and the tween yields. Covers sub-pixel
// rounding on scrollTop write-back, nothing more.
const PIN_TWEEN_YIELD_PX = 3;

/**
 * Append the shared stopped-response footer to a message bubble.
 * @param {HTMLElement} messageElement
 * @returns {void}
 */
export function appendStoppedIndicator(messageElement) {
  if (!messageElement) return;
  const contentDiv = messageElement.querySelector('.message__content');
  if (!contentDiv || contentDiv.querySelector('.message__stopped-indicator')) return;
  const indicator = document.createElement('div');
  indicator.className = 'message__stopped-indicator';
  indicator.textContent = 'User stopped response';
  contentDiv.appendChild(indicator);
}

export class MessageList {
  /**
   * @param {HTMLElement} messagesContainer - Container element for messages (messages__list)
   * @param {EventBus} eventBus - Event bus for module communication
   */
  constructor(messagesContainer, eventBus) {
    this.messages = messagesContainer;
    this.eventBus = eventBus;

    // Get the scroll container (.chat-window for full-width scrolling)
    this.scrollContainer = messagesContainer.closest('.chat-window');

    // Keep reference to .messages for padding adjustments
    this.messagesWrapper = messagesContainer.parentElement;

    // Message history tracking
    this.messageHistory = [];

    // Active response turn container (for grouping assistant responses)
    this.activeTurnContainer = null;

    // The message currently pinned to the top of the viewport (the send-time
    // reading frame). Owned by scrollToMessage/resetSpacer. `_pinPositioned`
    // records whether the frame was actually established; `_pinDeferred` records
    // that positioning was *attempted and failed* for want of geometry (a pin
    // taken against a hidden container), which is the only case recomputeSpacer
    // may adopt. A pin merely waiting on its own frame is not adoptable —
    // adopting it would position it instantly and eat the animated scroll.
    this._pinnedMessage = null;
    this._pinPositioned = false;
    this._pinDeferred = false;

    // Monotonic token for the pin tween: bumping it cancels any tween in flight
    // (a newer pin, a reset, teardown). `_pinTweenActive` suppresses the
    // recomputeSpacer re-pin while the tween owns scrollTop.
    this._pinTweenId = 0;
    this._pinTweenActive = false;

    // Lossless per-frame scheduler state: distinct callbacks queued in the same
    // frame all run (a single boolean flag would let a pending scroll tick
    // suppress a resize recompute).
    this._scheduledCallbacks = new Set();
    this._flushScheduled = false;
    this._boundButtonUpdate = () => this._updateScrollButtonVisibility();
    this._boundRecomputeSpacer = () => this.recomputeSpacer();
    this._boundTopEdgeCheck = () => this._checkTopEdgeReveal();

    // Touch-derived scroll intent (drives the mobile header-row collapse).
    // Direction comes from touch gestures, NOT scroll events: programmatic
    // scrolls (pin tween, scrollIntoView) fire scroll events indistinguishable
    // from the user's, but never fire touchmove.
    this._touchIntentY = null;
    this._touchIntentAccum = 0;
    this._lastIntentDirection = null;
    this.boundTouchStartHandler = null;
    this.boundTouchMoveHandler = null;

    // Scroll-to-bottom button
    this.scrollButton = null;
    this.boundScrollHandler = null;
    this.contentObserver = null;
    this.containerObserver = null;
    this.boundViewportHandler = null;
    this._createScrollButton();
    this._setupScrollListener();
    this._setupScrollIntentTracking();
    this._setupContentObserver();
    this._setupContainerObserver();
    this._setupViewportListener();
  }

  // ==================== RESPONSE TURN CONTAINER ====================

  /**
   * Create a new response turn container for grouping assistant content
   * All messages and components added while this container is active will be grouped inside it.
   * This enables CSS styling of the entire response as a unit.
   *
   * @param {string} jobId - Job ID for correlation
   * @returns {HTMLElement} The turn container element
   */
  createTurnContainer(jobId) {
    if (!this.messages) return null;

    const container = document.createElement('div');
    container.className = 'response-turn';
    container.dataset.jobId = jobId;

    this.messages.appendChild(container);
    this.activeTurnContainer = container;

    return container;
  }

  /**
   * Get the current active turn container, if any
   * @returns {HTMLElement|null} Active turn container or null
   */
  getActiveTurnContainer() {
    return this.activeTurnContainer;
  }

  /**
   * Clear the active turn container reference
   * Called when response stream completes. Container stays in DOM.
   */
  clearActiveTurnContainer() {
    this.activeTurnContainer = null;
  }

  /**
   * Get the appropriate container to append content to
   * Returns the active turn container if one exists, otherwise the messages container
   * @returns {HTMLElement} Container to append to
   * @private
   */
  _getAppendTarget() {
    return this.activeTurnContainer || this.messages;
  }

  /**
   * Scroll a message element to the top of the scrollable content area.
   * Reserves space below via a padding-bottom spacer on .messages so the
   * message can reach the top, then lets scrollIntoView + scroll-margin-top
   * position it (ChatGPT-style pinned reading frame). The element becomes the
   * tracked pinned message; recomputeSpacer() keeps the frame valid across
   * container resizes.
   * @param {HTMLElement} messageElement - The message element to scroll to
   * @param {Object} [options]
   * @param {'auto'|'smooth'} [options.behavior] - Override the motion-preference
   *   default. History replay pins instantly ('auto'); live sends animate.
   * @param {number} [options.duration] - Animate with the rAF pin tween over this
   *   many ms instead of native smooth scrolling. Use when the scroll overlaps a
   *   layout transition (see _animatePinScroll). Ignored when motion resolves to
   *   'auto'.
   */
  scrollToMessage(messageElement, { behavior, duration } = {}) {
    if (!messageElement) return;

    // scrollContainer is .chat-window (for scroll events/viewport)
    // messagesWrapper is .messages (for padding writes)
    const scrollContainer = this.scrollContainer;
    const messagesWrapper = this.messagesWrapper;
    if (!scrollContainer || !messagesWrapper) return;

    this._pinnedMessage = messageElement;
    this._pinPositioned = false;
    this._pinDeferred = false;
    this._cancelPinTween();

    // Use requestAnimationFrame to ensure DOM layout is complete
    requestAnimationFrame(() => {
      // Deferred one frame: the element may have been removed (unsend) or a
      // newer pin may have replaced this one meanwhile.
      if (!messageElement.isConnected || this._pinnedMessage !== messageElement) return;

      this._positionPin(messageElement, behavior || this._scrollBehavior(), duration);
    });
  }

  /**
   * Write the spacer and scroll the pinned message to the resting frame.
   * Returns false without touching the DOM when geometry is unavailable — a
   * pin taken against a hidden chat window (resume racing the dashboard→chat
   * transition); recomputeSpacer() completes it once the container is laid out.
   *
   * @param {HTMLElement} messageElement
   * @param {'auto'|'smooth'} behavior
   * @param {number} [duration] - Drive the scroll with the pin tween instead of
   *   native smooth scrolling (smooth motion only).
   * @returns {boolean} Whether the frame was positioned
   * @private
   */
  _positionPin(messageElement, behavior, duration) {
    const exactPadding = this._computeSpacerPadding(messageElement);
    if (exactPadding === null) {
      this._pinDeferred = true;
      return false;
    }
    this.messagesWrapper.style.paddingBottom = `${exactPadding}px`;

    if (behavior === 'smooth' && duration > 0) {
      this._animatePinScroll(messageElement, duration);
    } else {
      // Use native scrollIntoView with scroll-margin-top CSS
      messageElement.scrollIntoView({ behavior, block: 'start', inline: 'nearest' });
    }
    this._pinPositioned = true;
    this._pinDeferred = false;
    return true;
  }

  /**
   * Scroll the pinned message to its resting frame over `duration` ms, deriving
   * the destination afresh on every frame.
   *
   * Native `scrollIntoView({ behavior: 'smooth' })` samples its destination once,
   * so a scroll that deliberately overlaps a layout transition — the right panel
   * sliding out re-wraps every message as the chat widens — lands at an offset
   * that was already stale when it was measured. It is also cancelled outright by
   * any programmatic instant scroll, which the container observer issues while
   * that same transition runs. This tween is immune to both, and yields to real
   * user scrolling.
   *
   * @param {HTMLElement} messageElement
   * @param {number} duration - Tween length in ms
   * @private
   */
  _animatePinScroll(messageElement, duration) {
    const container = this.scrollContainer;
    const token = ++this._pinTweenId;
    this._pinTweenActive = true;

    const startedAt = performance.now();
    const from = container.scrollTop;
    let lastWritten = from;

    // The absolute scrollTop that rests the element at the frame. Expressed
    // relative to the *current* offset, so it stays correct as the layout moves.
    const restingScrollTop = () => container.scrollTop
      + messageElement.getBoundingClientRect().top
      - container.getBoundingClientRect().top
      - SCROLL_TOP_MARGIN;

    const stop = () => {
      if (token === this._pinTweenId) this._pinTweenActive = false;
    };

    const step = (now = performance.now()) => {
      // A newer pin/reset cancelled us, the element went away, or the user took
      // the scroller over.
      if (token !== this._pinTweenId || !messageElement.isConnected
        || this._pinnedMessage !== messageElement
        || Math.abs(container.scrollTop - lastWritten) > PIN_TWEEN_YIELD_PX) {
        stop();
        return;
      }

      const t = Math.min(1, (now - startedAt) / duration);
      // Ease-out cubic: most of the travel up front, settling into the frame.
      container.scrollTop = from + (restingScrollTop() - from) * (1 - Math.pow(1 - t, 3));
      lastWritten = container.scrollTop;

      if (t < 1) {
        requestAnimationFrame(step);
        return;
      }

      // Landed. The layout has finished moving by now, so re-derive the reserved
      // space against it — the padding written at pin time was measured against a
      // mid-transition layout.
      stop();
      const exactPadding = this._computeSpacerPadding(messageElement);
      if (exactPadding !== null) {
        this.messagesWrapper.style.paddingBottom = `${exactPadding}px`;
        container.scrollTop = restingScrollTop();
      }
    };

    requestAnimationFrame(step);
  }

  /**
   * Cancel any in-flight pin tween. Idempotent.
   * @private
   */
  _cancelPinTween() {
    this._pinTweenId++;
    this._pinTweenActive = false;
  }

  /**
   * Compute the spacer padding (px) that lets `messageElement` sit at the top
   * of the viewport with the remaining content below it.
   *
   * Measures rect-to-rect against `.messages__list` (this.messages), NOT the
   * `.messages` wrapper: the thinking indicator mounts below the list inside
   * the wrapper (always occupying its own fixed space, deliberately excluded
   * so scroll logic stays decoupled from indicator state), and the wrapper's
   * own padding-bottom — the previous spacer — also sits below the list. Both
   * are excluded from the measurement by construction.
   *
   * @param {HTMLElement} messageElement
   * @returns {number|null} Padding in px, or null when geometry is unavailable
   * @private
   */
  _computeSpacerPadding(messageElement) {
    const scrollContainer = this.scrollContainer;
    const list = this.messages;
    if (!scrollContainer || !list || !list.lastElementChild) return null;

    const viewportHeight = scrollContainer.clientHeight;
    if (viewportHeight === 0) return null; // hidden container (e.g. dashboard)

    const distance = list.getBoundingClientRect().bottom
      - messageElement.getBoundingClientRect().top;

    return Math.max(0, viewportHeight - SCROLL_TOP_MARGIN - distance);
  }

  /**
   * Re-derive the spacer for the current pinned message after the container
   * resizes (window resize, panel open/close, mobile keyboard). Only acts when
   * the user is still at the pinned frame — never shrinks padding under a
   * reader who scrolled elsewhere, since browser scroll clamping would yank
   * the view. Stale padding is harmless and is replaced on the next send.
   */
  recomputeSpacer() {
    const pinned = this._pinnedMessage;
    if (!pinned || !pinned.isConnected) return;
    const scrollContainer = this.scrollContainer;
    const messagesWrapper = this.messagesWrapper;
    if (!scrollContainer || !messagesWrapper) return;

    // Hidden container (dashboard navigation display:none): a 0×0 layout would
    // trivially pass the at-frame check below and wipe the frame.
    if (scrollContainer.clientHeight === 0) return;

    // A tween owns scrollTop and re-derives its own destination every frame —
    // an instant re-pin here would fight it and read as a jump.
    if (this._pinTweenActive) return;

    // Pin taken while the window was still hidden (history replay racing the
    // dashboard→chat transition): the frame was never established, so the
    // at-frame check below has nothing to measure against. This resize IS the
    // container becoming visible — position it now.
    //
    // Strictly `_pinDeferred`, never merely "not yet positioned": a pin waiting
    // on its own deferred frame is one rAF from positioning itself, and resize
    // ticks are dense while a panel animates. Adopting one here positioned it
    // instantly and the animated scroll that followed had nowhere left to
    // travel — the questionnaire artifact appeared to jump to the top.
    if (!this._pinPositioned) {
      if (!this._pinDeferred) return;
      this._positionPin(pinned, 'auto');
      return;
    }

    // At-frame check, centered on the resting offset (container top +
    // scroll-margin-top), not the container top itself.
    const restingTop = scrollContainer.getBoundingClientRect().top + SCROLL_TOP_MARGIN;
    const drift = pinned.getBoundingClientRect().top - restingTop;
    if (Math.abs(drift) > PIN_FRAME_TOLERANCE) return;

    const exactPadding = this._computeSpacerPadding(pinned);
    if (exactPadding === null) return;
    messagesWrapper.style.paddingBottom = `${exactPadding}px`;

    // Instant correction, not an animation.
    pinned.scrollIntoView({ behavior: 'auto', block: 'start', inline: 'nearest' });
  }

  /**
   * Clear the reserved-space spacer and forget the pinned message. The single
   * spacer-reset API — callers (clearMessages, unsend restore, future edit
   * flows) must use this rather than writing paddingBottom directly.
   */
  resetSpacer() {
    if (this.messagesWrapper) {
      this.messagesWrapper.style.paddingBottom = '';
    }
    this._pinnedMessage = null;
    this._pinPositioned = false;
    this._pinDeferred = false;
    this._cancelPinTween();
  }

  /**
   * Explicit scrollIntoView behavior honoring prefers-reduced-motion. The CSS
   * `scroll-behavior: auto !important` override does not affect explicit
   * smooth scrolls, so this must branch in JS (same guard as PillRow —
   * matchMedia is absent in jsdom).
   * @returns {'auto'|'smooth'}
   * @private
   */
  _scrollBehavior() {
    const reduced = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    return reduced ? 'auto' : 'smooth';
  }

  /**
   * Add a user message to the chat
   * @param {string} text - Message text
   * @param {Object} options - Optional configuration
   * @param {string[]} options.extraClasses - Additional CSS classes to add
   * @param {string} options.type - Message type for history tracking
   * @param {boolean} [options.scroll=true] - Scroll the new message into view. Set false for bulk history loads.
   * @returns {HTMLElement} Message element
   */
  addUserMessage(text, options = {}) {
    if (!this.messages) return null;
    if (!text && (!options.images || options.images.length === 0)) return null;

    // Create message container
    const messageDiv = document.createElement('div');
    messageDiv.className = 'message message--user';

    // Add any extra classes
    if (options.extraClasses && Array.isArray(options.extraClasses)) {
      options.extraClasses.forEach(cls => messageDiv.classList.add(cls));
    }

    // Create message content
    const contentDiv = document.createElement('div');
    contentDiv.className = 'message__content';

    // Render images if present
    if (options.images && options.images.length > 0) {
      const imagesContainer = this._renderMessageImages(options.images);
      contentDiv.appendChild(imagesContainer);
    }

    // Create paragraph with text (conditional — may be image-only)
    if (text) {
      const p = document.createElement('p');
      p.textContent = text;
      contentDiv.appendChild(p);
    }
    messageDiv.appendChild(contentDiv);

    // Add to messages container
    this.messages.appendChild(messageDiv);

    // Initialize truncation for long messages
    TruncationManager.initializeTruncation(messageDiv);

    // Scroll message to top of viewport (suppressible for bulk history loads)
    if (options.scroll !== false) {
      this.scrollToMessage(messageDiv);
    }

    // Add to message history
    const historyEntry = {
      role: 'user',
      content: text,
      element: messageDiv,
      timestamp: Date.now()
    };

    // Add type if specified for filtering
    if (options.type) {
      historyEntry.type = options.type;
    }

    this.messageHistory.push(historyEntry);

    return messageDiv;
  }

  /**
   * Remove a rendered message and its history entry. Truncation observers are
   * disconnected before DOM removal. Generic for unsend and future edit flows.
   *
   * @param {HTMLElement|null} element - Rendered message element
   * @returns {boolean} Whether an attached element was removed
   */
  removeMessage(element) {
    if (!element) return false;
    TruncationManager.destroyAll(element);
    this.messageHistory = this.messageHistory.filter(entry => (
      entry.element !== element && !element.contains(entry.element)
    ));
    if (this._pinnedMessage
        && (element === this._pinnedMessage || element.contains(this._pinnedMessage))) {
      this._pinnedMessage = null;
    }
    const wasConnected = element.isConnected;
    element.remove();
    return wasConnected;
  }

  /**
   * Add a divider message to the chat (e.g., mode switch indicators)
   * Creates a simple full-width divider with text and a horizontal line
   * @param {string} text - Divider text (e.g., "Switching to Advice Mode")
   * @returns {HTMLElement} Divider element
   */
  addDividerMessage(text, { scroll = true } = {}) {
    if (!text || !this.messages) return null;

    const dividerDiv = document.createElement('div');
    dividerDiv.className = 'message message--divider';

    const textSpan = document.createElement('span');
    textSpan.className = 'message__divider-text';
    textSpan.textContent = text;

    dividerDiv.appendChild(textSpan);
    this.messages.appendChild(dividerDiv);

    if (scroll) {
      this.scrollToMessage(dividerDiv);
    }

    return dividerDiv;
  }

  /**
   * Add an assistant message to the chat
   * @param {string} text - Message text (can be empty for streaming)
   * @param {Object} options - Rendering options
   * @param {boolean} options.streaming - Whether message is being streamed
   * @param {string} options.messageType - Message type for behavior control (e.g., 'system', 'welcome')
   * @param {string} options.customClass - Additional CSS class to add to message element
   * @returns {HTMLElement} Message element
   */
  addAssistantMessage(text = '', options = {}) {
    if (!this.messages) return null;

    // Create message container
    const messageDiv = document.createElement('div');
    messageDiv.className = 'message message--assistant';

    // Add streaming class if specified
    if (options.streaming) {
      messageDiv.classList.add('message--streaming');
    }

    // Add custom class if specified (e.g., 'message--orientation' for larger text)
    if (options.customClass) {
      messageDiv.classList.add(options.customClass);
    }

    // Store message type as data attribute for behavior control
    if (options.messageType) {
      messageDiv.dataset.messageType = options.messageType;
    }

    // Create message content
    const contentDiv = document.createElement('div');
    contentDiv.className = 'message__content';

    // Create div wrapper for assistant messages (supports markdown block elements)
    // Use div instead of p because markdown can contain ul, ol, blockquote, etc.
    if (text) {
      const div = document.createElement('div');
      div.className = 'message__text';
      div.textContent = text;
      contentDiv.appendChild(div);
    }

    messageDiv.appendChild(contentDiv);

    // Add to active turn container if one exists, otherwise messages container
    this._getAppendTarget().appendChild(messageDiv);

    // Do NOT scroll - let message appear below user message
    // User manually scrolls to read more if needed

    // Add to message history
    this.messageHistory.push({
      role: 'assistant',
      content: text || '',
      element: messageDiv,
      timestamp: Date.now()
    });

    return messageDiv;
  }

  /**
   * Add action bar with feedback buttons to an assistant message.
   * Action bar is placed in the turn container (if available) to appear after
   * all sibling content (e.g., questionnaire artifacts), using CSS order.
   * Falls back to appending inside the message if no turn container exists.
   * @param {HTMLElement} messageElement - The assistant message element
   * @private
   */
  _addActionBar(messageElement) {
    // Skip action bar for system-type messages (welcome, notifications, etc.)
    const skipTypes = ['system', 'welcome', 'notification', 'error', 'programmatic', 'discovery'];
    if (skipTypes.includes(messageElement.dataset.messageType)) return;

    // Skip action bar in discovery/orientation/intake modes — messages are agent-driven
    // and structured, and the buttons create awkward spacing before artifacts.
    // Also skip during the orientation advice session (love language discussion).
    const mode = window.modeOrchestrator?.getCurrentMode();
    if (mode === 'DISCOVERY' || mode === 'ORIENTATION' || mode === 'INTAKE') return;
    if (window.modeOrchestrator?._fromOrientationAdvice) return;

    // Generate unique ID for message if not present (used for action bar association)
    if (!messageElement.dataset.messageId) {
      messageElement.dataset.messageId = `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }
    const messageId = messageElement.dataset.messageId;

    // One bar per TURN (not per message): if the turn already has any action bar,
    // do not add a second. This makes "single bar per assistant turn" a structural
    // invariant rather than a convention that leans on every programmatic insert
    // remembering to tag itself. Multi-bubble turns don't occur on the live SSE
    // path today, but a stray untyped programmatic append near a live turn could
    // otherwise produce a second bar floated to the turn bottom by `order`.
    const turnContainer = messageElement.closest('.response-turn');
    const existing = (turnContainer || messageElement).querySelector('.message__actions');
    if (existing) return;

    const actionBar = document.createElement('div');
    actionBar.className = 'message__actions';
    actionBar.setAttribute('role', 'group');
    actionBar.setAttribute('aria-label', 'Message actions');
    // Store reference to associated message for event handlers
    actionBar.dataset.forMessage = messageId;

    actionBar.innerHTML = `
      <button class="message__action-btn" data-action="copy" data-tooltip="Copy" aria-label="Copy message">
        <svg width="16" height="16"><use href="#icon-copy"/></svg>
      </button>
      <button class="message__action-btn" data-action="thumbs-up" data-tooltip="Good response" aria-label="Good response">
        <svg width="16" height="16"><use href="#icon-thumbs-up"/></svg>
      </button>
      <button class="message__action-btn" data-action="thumbs-down" data-tooltip="Bad response" aria-label="Bad response">
        <svg width="16" height="16"><use href="#icon-thumbs-down"/></svg>
      </button>
    `;

    // Append to turn container if available (for correct visual ordering via CSS),
    // otherwise fall back to appending inside the message element
    if (turnContainer) {
      turnContainer.appendChild(actionBar);
    } else {
      messageElement.appendChild(actionBar);
    }

    // Set up tooltips for action buttons
    Tooltip.setupAll(actionBar, '[data-tooltip]', { position: 'below' });
  }

  /**
   * Set the complete text of a message (the all-at-once render path: polling
   * fallback + history replay). For assistant messages this is an authoritative
   * overwrite of the content container, rendering every embedded structured
   * block interleaved with prose via the shared {@link renderInterleavedBlocks}
   * walker — the SAME parser the transcript renderer uses, so the poll/reload
   * and transcript paths can't drift (INV-1).
   *
   * @param {HTMLElement} messageElement - Message element to update
   * @param {string} text - Complete message text
   * @param {{interactive?: boolean, skip?: Set<string>|null}} [opts]
   *   `interactive: true` for a fresh live turn (poll fallback) so proposal
   *   cards arm with `data-practice-id` and Accept/Reject can target them;
   *   `interactive: false` (default, safe) for history replay so already-decided
   *   proposals don't re-arm. `skip` omits tags entirely — pass
   *   {@link REPLAY_SKIP_TAGS} on replay so client-only lifecycle cards don't
   *   re-animate or re-fetch current state for an old message.
   */
  setMessageText(messageElement, text, { interactive = false, skip = null } = {}) {
    if (!messageElement || !text) return;

    const contentDiv = messageElement.querySelector('.message__content');
    if (!contentDiv) return;

    if (messageElement.classList.contains('message--assistant')) {
      // Authoritative overwrite. `replaceChildren` subsumes the old streamed
      // partials (`.message__block`) AND any stale `.message__text`/
      // `.structured-card`, so this is idempotent — calling it twice can't
      // duplicate content. The action bar lives on `messageElement` (not
      // `contentDiv`) and the thinking indicator is torn down before this runs,
      // so `contentDiv` holds only rendered content and is safe to fully replace.
      contentDiv.replaceChildren(renderInterleavedBlocks(text, { interactive, skip }));
      // Store readable markdown for the copy button — the SAME conversion the
      // live streaming path applies, so a poll fallback or reload can't turn
      // copy output into raw structured XML.
      messageElement.dataset.markdown = toCopyMarkdown(text);
    } else {
      let textElement = contentDiv.querySelector('p');
      if (!textElement) {
        textElement = document.createElement('p');
        contentDiv.appendChild(textElement);
      }
      // Plain text for user messages
      textElement.textContent = text;
    }

    // Update message history
    const historyEntry = this.messageHistory.find(
      msg => msg.element === messageElement
    );
    if (historyEntry) {
      historyEntry.content = text;
    }
  }

  /**
   * Add stopped indicator to a message
   * Shows "User stopped response" footer inside the message bubble
   * @param {HTMLElement} messageElement - Message element to add indicator to
   */
  addStoppedIndicator(messageElement) {
    appendStoppedIndicator(messageElement);
  }

  /**
   * Get current message history
   * @returns {Array} Message history array
   */
  getMessageHistory() {
    return this.messageHistory;
  }

  /**
   * Apply fade-in animation to an element.
   * Uses shared animation utility for consistency across components.
   * @param {HTMLElement} element - Element to animate
   * @param {number} [duration=200] - Animation duration in ms
   * @param {number} [slideDistance=6] - Vertical slide distance in px
   */
  fadeInElement(element, duration = 200, slideDistance = 6) {
    fadeIn(element, { duration, slideDistance });
  }

  /**
   * Add an interactive component to the chat (questionnaires, forms, etc.)
   * Components are assistant content and appear below without auto-scrolling.
   * Scrolling for user-like interactions (e.g., questionnaire completion) is
   * handled separately via scrollToMessage().
   *
   * @param {HTMLElement} componentElement - The component to add
   * @param {Object} metadata - Metadata about the component
   * @param {string} metadata.type - Component type (e.g., 'questionnaire', 'form')
   * @param {Object} metadata.data - Additional component data
   * @param {boolean} [metadata.animated=true] - Whether to animate entry with fade-in
   * @param {string} [metadata.renderLocation] - 'input' to mount in the chat input
   *   instead of the transcript (action-button groups only); explicit value wins over
   *   the button-group catalog.
   * @param {boolean} [metadata.allowTextEntry] - For input-mounted groups, whether to
   *   show the "respond in your own words" fallback link.
   * @param {string} [metadata.textEntryLabel] - Per-group override for that link's copy
   *   (e.g. the partner-memo review group's "Or, tell me what to change.").
   * @returns {HTMLElement|Object} Transcript container element, or — for input-mounted
   *   groups — a cleanup handle ({ isInputButtons, element, hide(), remove() }).
   */
  addInteractiveComponent(componentElement, metadata = {}) {
    if (!componentElement || !this.messages) return null;

    // Relocation: action-button groups flagged renderLocation:'input' are mounted in
    // the chat input region instead of the transcript. These are ephemeral — NOT added
    // to messageHistory (never replayed on reload). Returns a cleanup handle in place
    // of the transcript container.
    if (metadata.type === 'action-buttons' || metadata.type === 'buttons') {
      const inputHandle = this._maybeMountInputButtons(componentElement, metadata);
      if (inputHandle) return inputHandle;
      // else: not an input group (or input host unavailable) → transcript path below.
    }

    // Create container with message styling
    const container = document.createElement('div');
    container.className = 'message message--interactive';

    // Add type-specific class if provided
    if (metadata.type) {
      container.classList.add(`message--${metadata.type}`);
    }

    // Add component to container
    container.appendChild(componentElement);

    // Add to active turn container if one exists, otherwise messages container
    this._getAppendTarget().appendChild(container);

    // Apply fade-in animation (default: true for visual consistency with streamed content)
    if (metadata.animated !== false) {
      this.fadeInElement(container);
    }

    // NOTE: No scrollToMessage() here - components are assistant content
    // They appear below without auto-scrolling, like assistant text messages.
    // Scrolling for user-like interactions (e.g., after questionnaire completion)
    // is handled separately by the calling code.

    // Add to message history
    this.messageHistory.push({
      role: 'interactive',
      type: metadata.type || 'component',
      element: container,
      metadata: metadata.data || {},
      timestamp: Date.now()
    });

    return container;
  }

  /**
   * If an action-button group resolves to renderLocation:'input', mount it in the
   * chat input and return a cleanup handle. Returns null when the group is not an
   * input group OR the input host is unavailable (caller then uses the transcript).
   * @private
   * @param {HTMLElement} componentElement
   * @param {Object} metadata
   * @returns {{isInputButtons: true, element: HTMLElement, hide: Function, remove: Function}|null}
   */
  _maybeMountInputButtons(componentElement, metadata = {}) {
    const groupId = metadata.data?.groupId || null;
    if (this._resolveButtonOption(metadata, groupId, 'renderLocation', 'transcript') !== 'input') {
      return null;
    }

    const input = window.app?.modules?.input;
    if (!input || typeof input.showButtons !== 'function') return null;

    const allowTextEntry = this._resolveButtonOption(metadata, groupId, 'allowTextEntry', false) === true;
    const textEntryLabel = this._resolveButtonOption(metadata, groupId, 'textEntryLabel', null);
    input.showButtons(componentElement, { allowTextEntry, textEntryLabel, groupId });

    return {
      isInputButtons: true,
      element: componentElement,
      hide: () => input.hideButtons({ groupId }),
      remove: () => input.hideButtons({ groupId })
    };
  }

  /**
   * Resolve a button-group option (renderLocation / allowTextEntry): explicit per-call
   * metadata wins (top-level, then `metadata.data` where streamed/component producers
   * actually place options), else the button-group catalog (ButtonGroupRegistry), else
   * fallback.
   * @private
   */
  _resolveButtonOption(metadata, groupId, key, fallback) {
    if (metadata[key] !== undefined) return metadata[key];
    if (metadata.data?.[key] !== undefined) return metadata.data[key];
    if (groupId && typeof window.buttonGroupRegistry?.get === 'function') {
      const group = window.buttonGroupRegistry.get(groupId);
      if (group && group[key] !== undefined) return group[key];
    }
    return fallback;
  }

  /**
   * Get current message count
   * @returns {number} Number of messages
   */
  getMessageCount() {
    return this.messageHistory.length;
  }

  /**
   * Clear all messages and history
   */
  clearMessages() {
    if (this.messages) {
      TruncationManager.destroyAll(this.messages);
      this.messages.innerHTML = '';
    }
    this.messageHistory = [];

    // Clear the spacer so the next session doesn't inherit phantom scroll space
    this.resetSpacer();

    // Hide scroll button when messages are cleared
    this._hideScrollButton();
  }

  // ==================== SCROLL-TO-BOTTOM BUTTON ====================

  /**
   * Create the scroll-to-bottom button element
   * @private
   */
  _createScrollButton() {
    const button = document.createElement('button');
    button.className = 'scroll-to-bottom';
    button.setAttribute('aria-label', 'Scroll to bottom');
    button.setAttribute('type', 'button');

    // Simple chevron SVG (matches project icon style)
    button.innerHTML = `
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="6 9 12 15 18 9"></polyline>
      </svg>
    `;

    button.addEventListener('click', () => this._scrollToBottom());

    // Append to chat input - button positioned above the input with CSS
    const chatInput = document.querySelector('.chat-input');
    if (!chatInput) return;
    chatInput.appendChild(button);
    this.scrollButton = button;
  }

  /**
   * Queue a callback for the next animation frame. Distinct callbacks queued
   * in the same frame all run (lossless — a shared boolean flag would let a
   * pending scroll tick suppress a resize recompute); the same callback
   * queued twice runs once.
   * @param {Function} fn - Stable-identity callback (bound once in constructor)
   * @private
   */
  _schedule(fn) {
    this._scheduledCallbacks.add(fn);
    if (this._flushScheduled) return;
    this._flushScheduled = true;
    requestAnimationFrame(() => {
      const callbacks = [...this._scheduledCallbacks];
      this._scheduledCallbacks.clear();
      this._flushScheduled = false;
      callbacks.forEach(cb => cb());
    });
  }

  /**
   * Set up scroll listener, rAF-throttled via the shared scheduler
   * @private
   */
  _setupScrollListener() {
    this.boundScrollHandler = () => {
      this._schedule(this._boundButtonUpdate);
      this._schedule(this._boundTopEdgeCheck);
    };
    this.scrollContainer.addEventListener('scroll', this.boundScrollHandler);
  }

  /**
   * Emit user scroll intent ('down' | 'up') on `chat:scroll-intent`, derived
   * from touch gestures on the chat scroller. HeaderManager consumes this to
   * collapse/restore the header's dynamic-text row on phones. Touch, not
   * scroll: the pin tween and the scrollIntoView call sites produce scroll
   * events indistinguishable from the user's, but only a finger produces
   * touchmove. Touch-list access sticks to `touches[0]` / `touches.length`
   * (never Array methods) so the plain-array jsdom test mocks stay
   * representative of a real TouchList.
   * @private
   */
  _setupScrollIntentTracking() {
    if (!this.scrollContainer) return;

    // Accumulated finger travel must exceed this before a direction emits —
    // absorbs the few px of incidental jitter in an ordinary tap.
    const HYSTERESIS_PX = 12;

    this.boundTouchStartHandler = (event) => {
      if (event.touches.length !== 1) {
        this._touchIntentY = null;
        return;
      }
      this._touchIntentY = event.touches[0].clientY;
      this._touchIntentAccum = 0;
    };

    this.boundTouchMoveHandler = (event) => {
      // Two fingers = pinch-zoom (deliberately left enabled): touches[0]
      // translates independently of any scroll intent, so bail.
      if (event.touches.length !== 1 || this._touchIntentY === null) return;

      const y = event.touches[0].clientY;
      const delta = y - this._touchIntentY;
      this._touchIntentY = y;

      // Coalesced/duplicate touch samples arrive with no net travel; a zero
      // delta carries no direction and must not reach the reversal check
      // below (it would read as 'up' and wipe accumulated progress).
      if (delta === 0) return;

      // Finger travel and intended direction flip signs: finger up = scroll down.
      const direction = delta < 0 ? 'down' : 'up';
      if (direction !== (this._touchIntentAccum < 0 ? 'down' : 'up')) {
        this._touchIntentAccum = 0; // direction reversal restarts the count
      }
      this._touchIntentAccum += delta;

      if (Math.abs(this._touchIntentAccum) < HYSTERESIS_PX) return;
      this._touchIntentAccum = 0;
      this._emitScrollIntent(direction);
    };

    this.scrollContainer.addEventListener('touchstart', this.boundTouchStartHandler, { passive: true });
    this.scrollContainer.addEventListener('touchmove', this.boundTouchMoveHandler, { passive: true });
  }

  /**
   * Reveal backstop: parked at the very top of history (however the user got
   * there, including momentum), the row must be visible. Runs on the shared
   * rAF scheduler beside the button-visibility update.
   * @private
   */
  _checkTopEdgeReveal() {
    if (!this.scrollContainer) return;
    if (this.scrollContainer.scrollTop <= 4 && this._lastIntentDirection === 'down') {
      this._emitScrollIntent('up');
    }
  }

  /**
   * Emit `chat:scroll-intent` only when the direction actually changed.
   * @param {'down'|'up'} direction
   * @private
   */
  _emitScrollIntent(direction) {
    if (direction === this._lastIntentDirection) return;
    this._lastIntentDirection = direction;
    this.eventBus.emit('chat:scroll-intent', { direction });
  }

  /**
   * Set up ResizeObserver to detect content size changes
   * Updates scroll button visibility when messages grow (e.g., during streaming)
   * Industry-standard approach used by ChatGPT, Claude.ai, Slack, etc.
   * @private
   */
  _setupContentObserver() {
    if (!this.messagesWrapper || typeof ResizeObserver === 'undefined') return;

    this.contentObserver = new ResizeObserver(() => this._schedule(this._boundButtonUpdate));
    this.contentObserver.observe(this.messagesWrapper);
  }

  /**
   * Observe the scroll container's own box so viewport changes (window resize,
   * panel opens, keyboard resizes once the layout viewport tracks it) re-derive
   * the spacer and button visibility. Complements the content observer, which
   * watches the messages wrapper's content box and cannot see pure container
   * resizes.
   * @private
   */
  _setupContainerObserver() {
    if (!this.scrollContainer || typeof ResizeObserver === 'undefined') return;

    this.containerObserver = new ResizeObserver(() => {
      this._schedule(this._boundRecomputeSpacer);
      this._schedule(this._boundButtonUpdate);
    });
    this.containerObserver.observe(this.scrollContainer);
  }

  /**
   * iOS Safari fallback for the mobile keyboard: the layout viewport does not
   * resize there (interactive-widget is unsupported), so keyboard open/close
   * only fires visualViewport resize events. Feed them into the same spacer
   * recompute the container observer uses. Pinch-zoom also fires resize —
   * skipped via the scale check, since an instant re-pin mid-pinch is a
   * visible yank. No-op where visualViewport is unavailable (jsdom, old
   * browsers) — Android/desktop are covered by the container observer.
   * @private
   */
  _setupViewportListener() {
    if (!window.visualViewport) return;

    this.boundViewportHandler = () => {
      if (window.visualViewport.scale !== 1) return;
      this._schedule(this._boundRecomputeSpacer);
      this._schedule(this._boundButtonUpdate);
    };
    window.visualViewport.addEventListener('resize', this.boundViewportHandler);
  }

  /**
   * Update button visibility based on whether content extends below viewport
   * Uses element positions rather than scroll offsets to work correctly
   * regardless of dynamic padding set by scrollToMessage()
   * @private
   */
  _updateScrollButtonVisibility() {
    if (!this.scrollButton || !this.scrollContainer) return;

    const lastElement = this.messages.lastElementChild;
    if (!lastElement) {
      this.scrollButton.classList.remove('scroll-to-bottom--visible');
      return;
    }

    // Get effective viewport bottom (accounting for sticky chat input)
    const chatInput = document.querySelector('.chat-input');
    const chatInputTop = chatInput ? chatInput.getBoundingClientRect().top : window.innerHeight;

    const lastElementRect = lastElement.getBoundingClientRect();
    const threshold = 50;

    // Show button if bottom of last message is below the chat input
    if (lastElementRect.bottom > chatInputTop + threshold) {
      this.scrollButton.classList.add('scroll-to-bottom--visible');
    } else {
      this.scrollButton.classList.remove('scroll-to-bottom--visible');
    }
  }

  /**
   * Two-stage jump: from up in history, first restore the pinned reading frame
   * (the send-time "message at top" position); from at or past the frame — the
   * long-response overflow case — jump to the tail of the conversation.
   *
   * The frame target is the tracked pinned message (scrollToMessage also pins
   * dividers and questionnaire artifacts, so the pin — not "last user
   * message" — is the source of truth), falling back to the last user bubble
   * for resumed sessions where no pin was established, then to tail-only.
   * @private
   */
  _scrollToBottom() {
    const target = (this._pinnedMessage && this._pinnedMessage.isConnected)
      ? this._pinnedMessage
      : [...this.messages.querySelectorAll('.message--user')].pop() || null;

    if (target && this.scrollContainer) {
      const restingTop = this.scrollContainer.getBoundingClientRect().top + SCROLL_TOP_MARGIN;
      // Target below the resting line = user is above the frame (in history).
      if (target.getBoundingClientRect().top > restingTop + PIN_FRAME_TOLERANCE) {
        this.scrollToMessage(target);
        return;
      }
    }

    const lastElement = this.messages.lastElementChild;
    if (lastElement) {
      lastElement.scrollIntoView({ behavior: this._scrollBehavior(), block: 'end' });
    }
  }

  /**
   * Hide the scroll button (called on clearMessages)
   * @private
   */
  _hideScrollButton() {
    if (this.scrollButton) {
      this.scrollButton.classList.remove('scroll-to-bottom--visible');
    }
  }

  /**
   * Clean up scroll button, observers, and event listeners
   * Call this if MessageList is ever destroyed
   */
  destroy() {
    if (this.boundScrollHandler) {
      this.scrollContainer.removeEventListener('scroll', this.boundScrollHandler);
      this.boundScrollHandler = null;
    }

    if (this.boundTouchStartHandler) {
      this.scrollContainer.removeEventListener('touchstart', this.boundTouchStartHandler);
      this.boundTouchStartHandler = null;
    }

    if (this.boundTouchMoveHandler) {
      this.scrollContainer.removeEventListener('touchmove', this.boundTouchMoveHandler);
      this.boundTouchMoveHandler = null;
    }

    if (this.contentObserver) {
      this.contentObserver.disconnect();
      this.contentObserver = null;
    }

    if (this.containerObserver) {
      this.containerObserver.disconnect();
      this.containerObserver = null;
    }

    if (this.boundViewportHandler) {
      window.visualViewport?.removeEventListener('resize', this.boundViewportHandler);
      this.boundViewportHandler = null;
    }

    this._scheduledCallbacks.clear();
    this._pinnedMessage = null;
    this._cancelPinTween();

    if (this.scrollButton) {
      this.scrollButton.remove();
      this.scrollButton = null;
    }
  }

  /**
   * Render images in a user message bubble
   * @param {Array<{mimeType: string, data: string, filename: string}>} images
   * @returns {HTMLElement}
   * @private
   */
  _renderMessageImages(images) {
    const container = document.createElement('div');
    container.className = 'message__images';

    if (images.length === 1) {
      container.classList.add('message__images--single');
    } else {
      container.classList.add('message__images--grid');
    }

    for (const img of images) {
      const wrapper = document.createElement('div');
      wrapper.className = 'message__image-wrapper';

      const imgEl = document.createElement('img');
      imgEl.src = `data:${img.mimeType};base64,${img.data}`;
      imgEl.alt = img.filename || 'Attached image';
      imgEl.className = 'message__image';
      imgEl.loading = 'lazy';

      wrapper.appendChild(imgEl);
      container.appendChild(wrapper);
    }

    return container;
  }
}
